import { useEffect, useState, useRef } from "react";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Button,
} from "@/components/primitives";
import { CheckCircle, RefreshCw } from "lucide-react";
import {
	DailyCheckInAnalysisClean,
	DailyCheckInAnalysisRef,
} from "@/components/DailyCheckInAnalysisClean";
import { SuggestedReflection } from "./SuggestedReflection";
import { isSuggestedReflectionSimilar } from "../utils";
import { ANALYSIS_MIN_CHECKINS, ANALYSIS_CHECK_INTERVAL } from "../constants";
import type { Reflection, AnalysisData } from "../types";
import { useAuth } from "@/contexts/AuthContext";
import { ReflectionProgressMeter } from "@/components/ReflectionProgressMeter";

interface CompleteStepProps {
	totalCheckInsCount: number;
	reflections: Reflection[];
	analysisStatus: "idle" | "success" | "error";
	analysisMessage: string;
	generatingAnalysis: boolean;
	generatedAnalysis: AnalysisData | null;
}

function CompletionGauge() {
	const { user, profile } = useAuth();
	if (!user || !profile?.reflection_goal) return null;
	return (
		<div className="max-w-md mx-auto">
			<ReflectionProgressMeter
				userId={user.id}
				goal={profile.reflection_goal as any}
				celebrateOnSmashing
			/>
		</div>
	);
}

export function CompleteStep({
	totalCheckInsCount,
	reflections,
	analysisStatus,
	analysisMessage,
	generatingAnalysis,
	generatedAnalysis,
}: CompleteStepProps) {
	const analysisRef = useRef<DailyCheckInAnalysisRef>(null);
	const [showAnalysis, setShowAnalysis] = useState(false);
	const [fadeOutGauge, setFadeOutGauge] = useState(false);
	const [hideGauge, setHideGauge] = useState(false);
	const [showAnalysisDelayed, setShowAnalysisDelayed] = useState(false);

	useEffect(() => {
		if (totalCheckInsCount > ANALYSIS_MIN_CHECKINS && analysisRef.current) {
			const checkAnalysisState = () => {
				const state = analysisRef.current?.getState();
				// Show analysis if either:
				// 1. Database query shows it's available, OR
				// 2. We just generated it (analysisStatus === 'success' and generatedAnalysis exists)
				const shouldShowFromState = state
					? !state.loading &&
						!state.pending &&
						state.hasAnalysis &&
						!!state.lastUpdated &&
						!generatingAnalysis
					: false;
				const shouldShowFromGenerated =
					analysisStatus === "success" &&
					!!generatedAnalysis &&
					!generatingAnalysis;
				const shouldShow = shouldShowFromState || shouldShowFromGenerated;

				setShowAnalysis(shouldShow);
			};

			checkAnalysisState();
			const interval = setInterval(checkAnalysisState, ANALYSIS_CHECK_INTERVAL);
			return () => clearInterval(interval);
		} else {
			setShowAnalysis(false);
		}
	}, [
		totalCheckInsCount,
		generatingAnalysis,
		analysisStatus,
		generatedAnalysis,
	]);

	// Refresh the analysis component when analysis is successfully generated
	useEffect(() => {
		if (
			analysisStatus === "success" &&
			generatedAnalysis &&
			analysisRef.current
		) {
			// Trigger a refresh to pick up the new analysis from the database
			analysisRef.current.refresh();
		}
	}, [analysisStatus, generatedAnalysis]);

	// Crossfade: when analysis becomes available, fade out gauge then reveal analysis
	useEffect(() => {
		if (showAnalysis) {
			setFadeOutGauge(true);
			const t = setTimeout(() => {
				setHideGauge(true);
				setShowAnalysisDelayed(true);
			}, 400);
			return () => clearTimeout(t);
		}
	}, [showAnalysis]);

	const showSuggestedReflection =
		analysisStatus === "success" &&
		((generatedAnalysis?.reflectionSuggestions &&
			generatedAnalysis.reflectionSuggestions.some(
				(s) => !isSuggestedReflectionSimilar(s.title, reflections),
			)) ||
			(generatedAnalysis?.reflectionSuggestion &&
				!isSuggestedReflectionSimilar(
					generatedAnalysis.reflectionSuggestion.title,
					reflections,
				)));

	return (
		<Card className="max-w-4xl mx-auto border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
			<CardHeader className="text-center pb-6 border-b bg-gradient-to-r from-green-50/50 to-emerald-50/50 dark:from-green-950/20 dark:to-emerald-950/20">
				<div className="flex justify-center mb-4">
					<div className="p-4 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg">
						<CheckCircle className="h-12 w-12 text-white" />
					</div>
				</div>
				<CardTitle className="text-2xl font-bold text-foreground mb-2">
					Check-in Complete!
				</CardTitle>
				<CardDescription className="text-base">
					Thank you for taking the time to reflect on your day
				</CardDescription>
			</CardHeader>
			<CardContent className="text-center space-y-6 pt-8">
				{generatingAnalysis && (
					<div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-950/20">
						<div className="flex items-center justify-center space-y-2">
							<RefreshCw className="h-4 w-4 mr-2 animate-spin text-blue-600" />
							<span className="text-sm">{analysisMessage}</span>
						</div>
						<p className="text-xs text-gray-500 mt-2">
							This usually takes 30-60 seconds
						</p>
					</div>
				)}

				{/* Performance gauge (recalculated after check-in) */}
				{!hideGauge && (
					<div
						className={`transition-opacity duration-500 ${fadeOutGauge ? "opacity-0" : "opacity-100"}`}
					>
						<CompletionGauge />
					</div>
				)}

				{analysisStatus === "error" && (
					<div className="border rounded-lg p-4 bg-red-50 dark:bg-red-950/20">
						<div className="text-red-600 text-sm">
							<p className="font-medium">Analysis Failed</p>
							<p className="text-xs mt-1">{analysisMessage}</p>
						</div>
					</div>
				)}

				{showAnalysisDelayed && (
					<div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
						<DailyCheckInAnalysisClean
							ref={analysisRef}
							activeReflections={reflections}
							centerHeader
							initialAnalysis={generatedAnalysis || undefined}
						/>
					</div>
				)}

				{!showAnalysisDelayed && totalCheckInsCount > ANALYSIS_MIN_CHECKINS && (
					<div style={{ display: "none" }}>
						<DailyCheckInAnalysisClean
							ref={analysisRef}
							activeReflections={reflections}
							centerHeader
							initialAnalysis={generatedAnalysis || undefined}
						/>
					</div>
				)}

				<Button
					size="lg"
					asChild
					className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
				>
					<a href="/">Return to Dashboard</a>
				</Button>

				{showSuggestedReflection && generatedAnalysis && (
					<SuggestedReflection
						analysisData={generatedAnalysis}
						reflections={reflections}
					/>
				)}
			</CardContent>
		</Card>
	);
}
